/**
 * API-клиент задач colver (`/workspaces/{ws}/tasks`).
 * Паттерн идентичен `shared/api/workspaces`: axios + `X-Tests-User-Sid` в dev.
 */
import axios from 'axios';
import {readStoredSid} from '@api/auth';
import type {
  AddTaskCommentInput,
  BackendCreateTaskInput,
  BackendTask,
  BackendUpdateTaskInput,
  BackendTaskPage,
  TaskListInput,
  UpdateTaskCommentInput,
} from './types';

const http = axios.create({
  baseURL: import.meta.env.VITE_MEEREC_BASE ?? '/api/user/meerec/v1',
});

const fallbackDevSid = import.meta.env.VITE_DEV_USER_SID;
http.interceptors.request.use((config) => {
  const sid = readStoredSid() ?? fallbackDevSid;
  if (sid) config.headers.set('X-Tests-User-Sid', sid);
  return config;
});

export const tasksClient = {
  /**
   * Список задач воркспейса (новые сверху).
   *
   * Бэк (`colver/api/user/v1/tasks.py`) отдаёт голый `list[Task]`, а страницу
   * `{rows, nextCursor}` из контракта пока не реализует. Нормализуем оба
   * варианта: без этого `page.rows` === undefined и список падает с
   * «Не удалось загрузить задачи» при полностью успешном ответе 200.
   */
  async list(
    workspaceSid: string,
    input: TaskListInput = {},
  ): Promise<BackendTaskPage> {
    const {data} = await http.post<BackendTaskPage | BackendTask[]>(
      `/workspaces/${workspaceSid}/tasks`,
      input,
    );
    if (Array.isArray(data)) return {rows: data, nextCursor: null};
    return {
      ...data,
      rows: data.rows ?? [],
      nextCursor: data.nextCursor ?? null,
    };
  },

  /** Создать задачу. */
  async create(
    workspaceSid: string,
    input: BackendCreateTaskInput,
    idempotencyKey?: string,
  ): Promise<BackendTask> {
    const {data} = await http.put<BackendTask>(
      `/workspaces/${workspaceSid}/tasks`,
      input,
      {
        headers: idempotencyKey
          ? {'Idempotency-Key': idempotencyKey}
          : undefined,
      },
    );
    return data;
  },

  /** Карточка задачи. */
  async get(workspaceSid: string, taskSid: string): Promise<BackendTask> {
    const {data} = await http.get<BackendTask>(
      `/workspaces/${workspaceSid}/tasks/${taskSid}`,
    );
    return data;
  },

  /** Частичное обновление задачи. */
  async patch(
    workspaceSid: string,
    taskSid: string,
    input: BackendUpdateTaskInput,
  ): Promise<BackendTask> {
    const {data} = await http.patch<BackendTask>(
      `/workspaces/${workspaceSid}/tasks/${taskSid}`,
      input,
    );
    return data;
  },

  /** Удалить задачу (soft delete). */
  async remove(workspaceSid: string, taskSid: string): Promise<void> {
    await http.delete(`/workspaces/${workspaceSid}/tasks/${taskSid}`);
  },

  /** Добавить комментарий к задаче. */
  async addComment(
    workspaceSid: string,
    taskSid: string,
    input: AddTaskCommentInput,
    idempotencyKey: string,
  ): Promise<BackendTask> {
    const {data} = await http.post<BackendTask>(
      `/workspaces/${workspaceSid}/tasks/${taskSid}/comments`,
      input,
      {headers: {'Idempotency-Key': idempotencyKey}},
    );
    return data;
  },

  /** Изменить собственный комментарий; сервер возвращает актуальную задачу. */
  async updateComment(
    workspaceSid: string,
    taskSid: string,
    commentSid: string,
    input: UpdateTaskCommentInput,
  ): Promise<BackendTask> {
    const {data} = await http.patch<BackendTask>(
      `/workspaces/${workspaceSid}/tasks/${taskSid}/comments/${commentSid}`,
      input,
    );
    return data;
  },

  /** Удалить собственный комментарий; ответ 204 без тела. */
  async deleteComment(
    workspaceSid: string,
    taskSid: string,
    commentSid: string,
  ): Promise<void> {
    await http.delete(
      `/workspaces/${workspaceSid}/tasks/${taskSid}/comments/${commentSid}`,
    );
  },
};
